import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button } from 'antd';
import {
  deleteMe,
  getGmailStatus,
  getGmailAuthUrl,
  disconnectGmail,
} from '../../services/api';
import { ReplyCaptureTier, ForwardingNotifyMode } from '../../types';
import { AuthContext } from '../../components/auth/AuthContext';
import { LoadingContext } from '../../contexts/LoadingContext';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { showSuccessToastFullScreen, showErrorToastFullScreen } from '../../utils/toastUtils';
import { handleError, GMAIL_AUTH_EXPIRED_EVENT } from '../../utils/errorUtils';
import { signOut } from '../../utils/authUtils';
import { captureEvent } from '../../services/analytics';
import { setGmailReturnTo, clearAutoApplyResume } from '../../utils/gmailReturn';
import { useGmailConnectResult } from '../../hooks/useGmailConnectResult';
import { useGmailConsentPrimer } from '../../hooks/useGmailConsentPrimer';
import ModalContainer from '../../components/modals/ModalContainer';
import ChangePasswordSection from '../../components/settings/ChangePasswordSection';
import SearchPreferencesSection from '../../components/settings/SearchPreferencesSection';
import AutoApplySection from '../../components/settings/AutoApplySection';
import ReplyCaptureTierList from '../../components/email/ReplyCaptureTierList';
import ForwardingNotifyModeSelector from '../../components/email/ForwardingNotifyModeSelector';
import { getGmailAddonUrl, getForwardEmail } from '../../utils/replyCaptureStorage';
import css from './Settings.module.scss';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { setIsLoading } = useContext(LoadingContext);
  const userFirstName = localStorage.getItem('userFirstName');

  const [gmailConnected, setGmailConnected] = useState(false);
  // Defaults to true so the "sending isn't enabled" warning only ever appears
  // on an explicit canSend: false from the backend (older payloads omit it).
  const [gmailCanSend, setGmailCanSend] = useState(true);
  const [gmailAddress, setGmailAddress] = useState<string | null>(null);
  const [gmailLoading, setGmailLoading] = useState(true);
  // Whether the GET actually answered. A failure leaves connected=false, which
  // is indistinguishable from a real "no Gmail" unless we track it — and the
  // Auto-Apply card spends a one-shot resume flag on that distinction.
  const [gmailStatusKnown, setGmailStatusKnown] = useState(false);

  const [notifyMode, setNotifyMode] = useState<ForwardingNotifyMode>('auto');
  const [notifySaving, setNotifySaving] = useState(false);

  // One app-wide GET feeds every settings surface here (notify mode, search
  // preferences, Auto-Apply) and the offer cards on other pages. Null means "we
  // don't know your settings" — in flight or failed — and the sections below
  // stay inert until it holds real values, so nothing can save a placeholder
  // default over what the user actually has stored.
  const {
    settings: userSettings,
    loading: settingsLoading,
    updateSettings,
    applySettings,
  } = useUserSettings();

  const forwardingAddress = getForwardEmail();

  // The Auto-Apply nudge email links to /settings?autoApply=setup — land with
  // the activation modal already open, then drop the param so a refresh or
  // back-navigation doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenSetup = searchParams.get('autoApply') === 'setup';

  // Handles ?gmail=connected/error when this page is the connect origin; relays
  // back to the dashboard when it isn't. Status refresh is covered by the
  // mount effect below (the backend redirect is a full page load).
  useGmailConnectResult();

  useEffect(() => {
    loadGmailStatus();
  }, []);

  // Seed the notify-mode radio once the shared settings land (and re-seed if a
  // save elsewhere changes them).
  useEffect(() => {
    if (userSettings) setNotifyMode(userSettings.forwardingNotifyMode);
  }, [userSettings]);

  const loadGmailStatus = async () => {
    try {
      const status = await getGmailStatus();
      setGmailConnected(status.connected);
      setGmailCanSend(status.canSend !== false);
      setGmailAddress(status.gmailAddress);
      setGmailStatusKnown(true);
    } catch (error) {
      console.error('Failed to load Gmail status:', error);
    } finally {
      setGmailLoading(false);
    }
  };

  const handleNotifyModeChange = async (next: ForwardingNotifyMode) => {
    if (next === notifyMode || notifySaving) return;
    const previous = notifyMode;
    setNotifyMode(next); // optimistic
    setNotifySaving(true);
    try {
      const settings = await updateSettings({ forwardingNotifyMode: next });
      setNotifyMode(settings.forwardingNotifyMode);
      captureEvent('forwarding_notify_mode_changed', {
        previous_mode: previous,
        new_mode: settings.forwardingNotifyMode,
      });
    } catch (error) {
      setNotifyMode(previous); // rollback
      handleError(error);
    } finally {
      setNotifySaving(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      setGmailReturnTo('/settings'); // stay on Settings; overwrites any stale origin
      // This is a plain "Connect Gmail", not an Auto-Apply flow. An abandoned
      // Auto-Apply attempt within the last 10 minutes would otherwise still be
      // pending, and THIS connect would spring its setup modal.
      clearAutoApplyResume();
      const { authUrl } = await getGmailAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      handleError(error);
    }
  };

  // Every path to Google goes through the consent primer first; Continue runs
  // the untouched redirect above. The 'fix' variant re-runs the same flow when
  // the user connected without checking "Send email on your behalf".
  const { openPrimer, primer } = useGmailConsentPrimer(handleConnectGmail);

  const handleDisconnectGmail = async () => {
    try {
      await disconnectGmail();
      setGmailStatusKnown(true);
      setGmailConnected(false);
      // NOT reset to true. `true` is the "unknown/legacy payload" default for a
      // CONNECTED account; asserting it for an account we just disconnected
      // reports a send capability that no longer exists, and the Auto-Apply card
      // reads this to decide whether to warn an enabled user.
      setGmailCanSend(false);
      setGmailAddress(null);
      showSuccessToastFullScreen('Gmail disconnected.');
      captureEvent('gmail_disconnected', {});
    } catch (error) {
      handleError(error);
    }
  };

  const handleTierSetup = (tier: ReplyCaptureTier) => {
    if (tier === 'addon') {
      window.open(getGmailAddonUrl(), '_blank', 'noopener');
      return;
    }
    if (tier === 'forward') {
      window.open('https://www.jobbeast.org/email-integration/manual-forward', '_blank', 'noopener');
      return;
    }
  };

  useEffect(() => {
    const onExpired = () => {
      setGmailStatusKnown(true);
      setGmailConnected(false);
      setGmailCanSend(false);
      setGmailAddress(null);
      showErrorToastFullScreen('Your Gmail connection expired. Please reconnect.');
    };
    window.addEventListener(GMAIL_AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(GMAIL_AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const deleteUser = async () => {
    try {
      setIsLoading(true);
      const userDeletionResponse = await deleteMe();
      console.info(`User Deleted. Sorry to see you go, ${userFirstName}. User Id: ${userDeletionResponse.id}`);
      showSuccessToastFullScreen('Account deleted.', 500);
      captureEvent('account_deleted', {});
      await new Promise((resolve) => setTimeout(resolve, 1000));
      signOut(navigate);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccountButtonClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    console.log(`Deleting user ${userId}...`);
    setShowConfirmation(false);
    try {
      await deleteUser();
    } catch (error) {
      console.error(error);
      showErrorToastFullScreen('An error occurred while deleting your account. Please try again later.');
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmation(false);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showConfirmation) {
        handleCancelDelete();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showConfirmation]);

  return (
    <div className={css.settings}>
      <h1>General Settings</h1>

      <section className={css.section}>
        <h2>Connected Accounts</h2>
        <div className={css.gmailCard}>
          <div className={css.gmailInfo}>
            <span className={css.gmailLabel}>Gmail</span>
            {gmailLoading ? (
              <span className={css.gmailStatus}>Loading...</span>
            ) : gmailConnected ? (
              <span className={`${css.gmailStatus} ${css.connected}`}>{gmailAddress}</span>
            ) : (
              <span className={`${css.gmailStatus} ${css.disconnected}`}>Not connected</span>
            )}
          </div>
          <div className={css.gmailActions}>
            {!gmailLoading && (
              gmailConnected ? (
                <button className={css.disconnectButton} onClick={handleDisconnectGmail}>
                  Disconnect
                </button>
              ) : (
                <button className={css.connectButton} onClick={() => openPrimer('connect')}>
                  Connect Gmail
                </button>
              )
            )}
          </div>
        </div>
        {!gmailLoading && gmailConnected && !gmailCanSend && (
          <Alert
            type="warning"
            showIcon
            className={css.sendWarning}
            message="Connected, but sending isn't enabled — we can't send applications yet."
            action={
              <Button size="small" onClick={() => openPrimer('fix')}>
                Fix permission
              </Button>
            }
          />
        )}
        <p className={css.sectionDescription}>
          Connect your Gmail to send job applications directly from your email address.
          Emails will appear in your Sent folder and replies will come back to you.
        </p>
      </section>

      <SearchPreferencesSection
        settings={userSettings}
        loading={settingsLoading}
        onSaved={applySettings}
      />

      <AutoApplySection
        settings={userSettings}
        loading={settingsLoading}
        onSaved={applySettings}
        // undefined = we do not know. A transient GET failure must not read as
        // "disconnected" — the card would warn wrongly and burn the resume flag.
        gmailConnected={gmailStatusKnown ? gmailConnected : undefined}
        gmailCanSend={gmailCanSend}
        gmailLoading={gmailLoading}
        autoOpenSetup={autoOpenSetup}
        onAutoOpened={() => {
          // Consume only our own param — the nudge email carries utm_* tags,
          // and ?gmail=… can still be in flight alongside it.
          const next = new URLSearchParams(searchParams);
          next.delete('autoApply');
          setSearchParams(next, { replace: true });
        }}
      />

      <section className={css.section}>
        <h2>Reply Capture</h2>
        <p className={css.sectionLead}>
          Choose how Job Beast learns about responses to your applications.
        </p>
        <ReplyCaptureTierList
          forwardingAddress={forwardingAddress}
          onSetup={handleTierSetup}
        />
        <p className={css.captureStatus}>
          We'll show your replies in the app as they're forwarded.
        </p>
        <ForwardingNotifyModeSelector
          value={notifyMode}
          // Same rule as the sections above: 'auto' is a placeholder until
          // the GET lands, and saving it would overwrite the stored mode.
          disabled={notifySaving || !userSettings}
          onChange={handleNotifyModeChange}
        />
      </section>

      <ChangePasswordSection />

      <section className={css.section}>
        <h2>Account Management</h2>
        <div className={css.accountManagement}>
          <p>Permanently delete your account and all associated data.</p>
          <button className={css.deletionButton} onClick={handleDeleteAccountButtonClick}>
            Delete Account
          </button>
        </div>
      </section>

      <ModalContainer isOpen={showConfirmation} onClose={handleCancelDelete}>
        <div className={css.modalContent}>
          <p>Are you sure you want to delete your account? This action is irreversible.</p>
          <div className={css.modalButtons}>
            <button className={css.confirmButton} onClick={handleConfirmDelete}>
              Yes, delete my account
            </button>
            <button className={css.cancelButton} onClick={handleCancelDelete}>
              Cancel
            </button>
          </div>
        </div>
      </ModalContainer>

      {primer}
    </div>
  );
};

export default Settings;
